import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Menu, Spin, Badge, Tag } from 'antd';
import {
  AuditOutlined, CheckCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import AppHeaderHomePageTemplate from '../Organism/AppHeaderHomePageTemplate';
import MainContent from '../Templates/MainContent';
import UserGrid from '../Templates/UserGrid';
import Footer from '../Atoms/Footer';
import Progress from '../Molecules/Progress';

const { Sider, Content } = Layout;

interface ReviewMenuItem {
  key: string;
  label: string;
  path: string;
  stage: string;
  stageName: string;
  currentStage: number;
}

interface MenuGroup {
  title: string;
  items: ReviewMenuItem[];
}

const PATH_CONFIG: Record<string, { stage: string; stageName: string; currentStage: number; groupTitle: string }> = {
  '/review-hub/pm/individual':     { stage: '0', stageName: 'Individual Award Nominees', currentStage: 1, groupTitle: 'Manager Review' },
  '/review-hub/pm/team':           { stage: '3', stageName: 'Team Award Nominees',       currentStage: 1, groupTitle: 'Manager Review' },
  '/review-hub/level1/individual': { stage: '1', stageName: 'Individual Award Nominees', currentStage: 2, groupTitle: 'Unit Head Review' },
  '/review-hub/level1/team':       { stage: '4', stageName: 'Team Award Nominees',       currentStage: 2, groupTitle: 'Unit Head Review' },
  '/review-hub/level2/individual': { stage: '2', stageName: 'Individual Award Nominees', currentStage: 3, groupTitle: 'ELT Review' },
  '/review-hub/level2/team':       { stage: '5', stageName: 'Team Award Nominees',       currentStage: 3, groupTitle: 'ELT Review' },
  '/review-hub/stack/individual':  { stage: '6', stageName: 'Individual Award Nominees', currentStage: 4, groupTitle: 'ELT Calibration' },
  '/review-hub/final/individual':  { stage: '7', stageName: 'Individual Award Nominees', currentStage: 5, groupTitle: 'Final Review' },
};

const pending = (vote: string | null | undefined) => vote !== 'Approved' && vote !== 'Rejected';

const ReviewHubPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { instance, accounts } = useMsal();

  const [userRoles, setUserRoles]               = useState<string[]>([]);
  const [collapsed, setCollapsed]               = useState(false);
  const [loading, setLoading]                   = useState(true);
  const [isManager, setIsManager]               = useState(false);
  const [isFirstLevelApprover, setIsFirstLevelApprover]   = useState(false);
  const [isSecondLevelApprover, setIsSecondLevelApprover] = useState(false);
  const [isFinalLevelApprover, setIsFinalLevelApprover]   = useState(false);
  const [isSuperAdmin, setIsSuperAdmin]                   = useState(false);
  const [pendingCounts, setPendingCounts]       = useState<Record<string, number>>({});

  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return res.idToken;
  }, [instance, accounts]);

  useEffect(() => {
    const saved = localStorage.getItem('userRoles');
    setUserRoles(saved ? JSON.parse(saved) : []);
  }, []);

  useEffect(() => {
    const detect = async () => {
      const userEmail = localStorage.getItem('UserMail');
      if (!userEmail) { setLoading(false); return; }

      try {
        const token   = await fetchToken();
        const baseURL = process.env.REACT_APP_API_URL;
        const res     = await fetch(`${baseURL}/api/nominations/users/${userEmail}/manager`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { setLoading(false); return; }

        const data = await res.json();
        const all: any[] = data.responseData ?? [];

        setIsManager(all.some(i => i.managerEmail === userEmail));
        setIsFirstLevelApprover(all.some(i => i.firstLevelApproverEmail === userEmail));
        setIsSecondLevelApprover(all.some(i => i.secondLevelApproverEmailId === userEmail));
        setIsFinalLevelApprover(all.some(i => i.finalLevelApproverEmail === userEmail));

        // Skip logic — mirrors MainContent.tsx
        // Scenario 2: Manager is the nominator → skip manager stage
        // Scenario 3: Unit Head is the nominator → skip manager + unit head stages
        // Scenario 4: ELT (secondLevelApprover) is the nominator → skip manager + unit head stages
        const managerSkipped    = (i: any) => i.nominatorEmail === i.managerEmail;
        const unitHeadSkipped   = (i: any) => i.nominatorEmail === i.firstLevelApproverEmail;
        const eltNominated      = (i: any) => i.nominatorEmail === i.secondLevelApproverEmailId;

        const managerCleared    = (i: any) =>
          i.managerVote === 'Approved' || managerSkipped(i) || unitHeadSkipped(i) || eltNominated(i);
        const unitHeadCleared   = (i: any) =>
          i.firstLevelApproverVote === 'Approved' || unitHeadSkipped(i) || eltNominated(i);

        // Compute per-route pending counts (awaiting THIS user's vote, respecting skip logic)
        setPendingCounts({
          '/review-hub/pm/individual': all.filter(i =>
            i.managerEmail === userEmail &&
            i.rewardName === 'ACE Individual Award' &&
            !managerSkipped(i) &&          // skip if manager is the nominator
            pending(i.managerVote)
          ).length,
          '/review-hub/pm/team': all.filter(i =>
            i.managerEmail === userEmail &&
            i.rewardName === 'ACE Team Award' &&
            !managerSkipped(i) &&
            pending(i.managerVote)
          ).length,
          '/review-hub/level1/individual': all.filter(i =>
            i.firstLevelApproverEmail === userEmail &&
            i.rewardName === 'ACE Individual Award' &&
            managerCleared(i) &&           // prior stage approved or skipped
            !unitHeadSkipped(i) &&         // skip if unit head is the nominator
            pending(i.firstLevelApproverVote)
          ).length,
          '/review-hub/level1/team': all.filter(i =>
            i.firstLevelApproverEmail === userEmail &&
            i.rewardName === 'ACE Team Award' &&
            managerCleared(i) &&
            !unitHeadSkipped(i) &&
            pending(i.firstLevelApproverVote)
          ).length,
          '/review-hub/level2/individual': all.filter(i =>
            i.secondLevelApproverEmailId === userEmail &&
            i.rewardName === 'ACE Individual Award' &&
            managerCleared(i) &&           // manager stage cleared
            unitHeadCleared(i) &&          // unit head stage cleared
            pending(i.secondLevelApproverVote)
          ).length,
          '/review-hub/level2/team': all.filter(i =>
            i.secondLevelApproverEmailId === userEmail &&
            i.rewardName === 'ACE Team Award' &&
            managerCleared(i) &&
            unitHeadCleared(i) &&
            pending(i.secondLevelApproverVote)
          ).length,
          '/review-hub/stack/individual': all.filter(i =>
            i.finalLevelApproverEmail === userEmail &&
            i.secondLevelApproverVote === 'Approved' &&
            pending(i.finalLevelApproverVote)
          ).length,
        });

        // Check super admin access for Final Review (stage 7)
        const adminRes = await fetch(`${baseURL}/api/nominations/users/${userEmail}/admin`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (adminRes.ok) {
          const roleCode = await adminRes.json();
          const isSA = roleCode === 5;
          setIsSuperAdmin(isSA);

          if (isSA) {
            const allNomRes = await fetch(`${baseURL}/api/nominations`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (allNomRes.ok) {
              const allNomData = await allNomRes.json();
              const allNoms: any[] = allNomData.responseData ?? allNomData ?? [];
              setPendingCounts(prev => ({
                ...prev,
                '/review-hub/final/individual': allNoms.filter(i =>
                  i.rewardName === 'ACE Individual Award' &&
                  i.secondLevelApproverVote === 'Approved' &&
                  pending(i.finalLevelApproverVote)
                ).length,
              }));
            }
          }
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    detect();
  }, [fetchToken]);

  const menuGroups: MenuGroup[] = useMemo(() => {
    const groups: MenuGroup[] = [];

    if (isManager) {
      groups.push({
        title: 'Manager Review',
        items: [
          { key: '/review-hub/pm/individual', label: 'Individual Awards', path: '/review-hub/pm/individual', stage: '0', stageName: 'Individual Award Nominees', currentStage: 1 },
          { key: '/review-hub/pm/team',       label: 'Team Awards',       path: '/review-hub/pm/team',       stage: '3', stageName: 'Team Award Nominees',       currentStage: 1 },
        ],
      });
    }

    if (userRoles.includes('unithead') || isFirstLevelApprover) {
      groups.push({
        title: 'Unit Head Review',
        items: [
          { key: '/review-hub/level1/individual', label: 'Individual Awards', path: '/review-hub/level1/individual', stage: '1', stageName: 'Individual Award Nominees', currentStage: 2 },
          { key: '/review-hub/level1/team',       label: 'Team Awards',       path: '/review-hub/level1/team',       stage: '4', stageName: 'Team Award Nominees',       currentStage: 2 },
        ],
      });
    }

    if (userRoles.includes('elt') || isSecondLevelApprover) {
      groups.push({
        title: 'ELT Review',
        items: [
          { key: '/review-hub/level2/individual', label: 'Individual Awards', path: '/review-hub/level2/individual', stage: '2', stageName: 'Individual Award Nominees', currentStage: 3 },
          { key: '/review-hub/level2/team',       label: 'Team Awards',       path: '/review-hub/level2/team',       stage: '5', stageName: 'Team Award Nominees',       currentStage: 3 },
        ],
      });
    }

    if (userRoles.includes('elt') || isSecondLevelApprover || isFinalLevelApprover) {
      groups.push({
        title: 'ELT Calibration',
        items: [
          { key: '/review-hub/stack/individual', label: 'Individual Awards', path: '/review-hub/stack/individual', stage: '6', stageName: 'Individual Award Nominees', currentStage: 4 },
        ],
      });
    }

    if (isSuperAdmin) {
      groups.push({
        title: 'Final Review',
        items: [
          { key: '/review-hub/final/individual', label: 'Individual Awards', path: '/review-hub/final/individual', stage: '7', stageName: 'Individual Award Nominees', currentStage: 5 },
        ],
      });
    }

    return groups;
  }, [userRoles, isManager, isFirstLevelApprover, isSecondLevelApprover, isFinalLevelApprover, isSuperAdmin]);

  const allItems = useMemo(() => menuGroups.flatMap(g => g.items), [menuGroups]);

  const selectedKey    = location.pathname;
  const currentConfig  = PATH_CONFIG[selectedKey];
  const currentPending = pendingCounts[selectedKey] ?? 0;

  // Total pending across all sections (for the sidebar header)
  const totalPending = useMemo(
    () => Object.values(pendingCounts).reduce((sum, n) => sum + n, 0),
    [pendingCounts]
  );

  useEffect(() => {
    if (loading || location.pathname !== '/review-hub' || allItems.length === 0) return;
    const unitHeadItem = allItems.find(i => i.key.includes('level1'));
    navigate((unitHeadItem ?? allItems[0]).path, { replace: true });
  }, [loading, allItems, location.pathname, navigate]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeaderHomePageTemplate />

      <Layout style={{ minHeight: 'calc(100vh - 50px)' }}>

        {/* ── Sidebar ── */}
        <Sider
          width={230}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          style={{ background: '#481558' }}
        >
          {/* Sidebar header strip */}
          <div style={{
            padding: collapsed ? '16px 8px' : '16px',
            borderBottom: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <AuditOutlined style={{ color: '#E69CFF', fontSize: 20, flexShrink: 0 }} />
            {!collapsed && (
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>Review Hub</div>
                {totalPending > 0 && (
                  <div style={{ color: '#E69CFF', fontSize: 11, marginTop: 2 }}>
                    {totalPending} pending action{totalPending !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
              <Spin />
            </div>
          ) : (
            <Menu
              mode="inline"
              theme="dark"
              selectedKeys={[selectedKey]}
              style={{ background: '#481558', marginTop: 8, borderRight: 0 }}
              onClick={({ key }) => navigate(key)}
            >
              {menuGroups.map(group => (
                <Menu.ItemGroup
                  key={group.title}
                  title={
                    <span style={{
                      color: '#E69CFF',
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 1.2,
                    }}>
                      {group.title}
                    </span>
                  }
                >
                  {group.items.map(item => {
                    const count = pendingCounts[item.key] ?? 0;
                    return (
                      <Menu.Item
                        key={item.key}
                        style={{ color: '#fff', paddingLeft: collapsed ? undefined : 24 }}
                      >
                        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{item.label}</span>
                          {count > 0 && (
                            <Badge
                              count={count}
                              size="small"
                              style={{ backgroundColor: '#8629A4', marginLeft: 6 }}
                            />
                          )}
                        </span>
                      </Menu.Item>
                    );
                  })}
                </Menu.ItemGroup>
              ))}
            </Menu>
          )}
        </Sider>

        {/* ── Main content ── */}
        <Content style={{ background: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Spin size="large" tip="Loading your review sections..." />
            </div>

          ) : currentConfig ? (
            <>
              {/* Content header */}
              <div style={{
                background: '#fff',
                padding: '14px 24px 0',
                borderBottom: '1px solid #f0f0f0',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a' }}>
                      {currentConfig.groupTitle}
                      <span style={{ color: '#888', fontWeight: 400, fontSize: 14, marginLeft: 8 }}>
                        — {currentConfig.stageName}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                      Nominations awaiting your review
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 2 }}>
                    {currentPending > 0 ? (
                      <Tag icon={<ClockCircleOutlined />} color="purple">
                        {currentPending} pending
                      </Tag>
                    ) : (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        All reviewed
                      </Tag>
                    )}
                  </div>
                </div>

                <Progress currentStage={currentConfig.currentStage} onStepClick={() => {}} />
              </div>

              {/* Data */}
              <div style={{ flex: 1, background: '#fff', marginTop: 1 }}>
                {currentConfig.stage === '6' ? (
                  <UserGrid stage="6" />
                ) : (
                  <MainContent stage={currentConfig.stage} stageName={currentConfig.stageName} />
                )}
              </div>
            </>

          ) : (
            /* Empty / landing state */
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, padding: 32 }}>
              <div style={{
                textAlign: 'center',
                background: '#fff',
                borderRadius: 12,
                padding: '40px 48px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                maxWidth: 420,
              }}>
                <AuditOutlined style={{ fontSize: 48, color: '#481558', marginBottom: 16 }} />
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', marginBottom: 8 }}>
                  Welcome to Review Hub
                </div>
                <div style={{ fontSize: 13, color: '#888', lineHeight: 1.6 }}>
                  Select a section from the left menu to start reviewing nominations.
                  {totalPending > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <Tag color="purple">{totalPending} pending action{totalPending !== 1 ? 's' : ''} across all sections</Tag>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Content>
      </Layout>

      <Footer />
    </Layout>
  );
};

export default ReviewHubPage;
